import React, { useState, useEffect, useCallback } from 'react';
import {
  MessageSquare, CreditCard, Settings as SettingsIcon, History,
  Send, Bell, ChevronRight, AlertTriangle, Loader2, Package,
} from 'lucide-react';
import Shell from '../dashboard/Shell';
import Modal from '../../components/ui/Modal';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useToast } from '../../components/ui/Toast';
import './SMSPage.css';

const TABS = [
  { id: 'dashboard', label: 'Dashboard',  icon: MessageSquare },
  { id: 'credits',   label: 'Buy credits', icon: CreditCard },
  { id: 'settings',  label: 'Settings',   icon: SettingsIcon },
  { id: 'history',   label: 'History',    icon: History },
];

const NOTIFICATIONS = [
  {
    key: 'orderCreatedEnabled',
    name: 'Order created',
    description: 'Send SMS when a new order is placed',
    template: 'Hi {firstName}, your order #{orderNumber} has been received. We\'ll notify you when it ships!',
  },
  {
    key: 'orderShippedEnabled',
    name: 'Order shipped',
    description: 'Send SMS when order is shipped',
    template: 'Hi {firstName}, your order #{orderNumber} has been shipped and is on its way!',
  },
  {
    key: 'orderDeliveredEnabled',
    name: 'Order delivered',
    description: 'Send SMS when order is delivered',
    template: 'Hi {firstName}, your order #{orderNumber} has been delivered. Enjoy!',
  },
  {
    key: 'orderCompletedEnabled',
    name: 'Order completed',
    description: 'Send SMS when order is completed',
    template: 'Hi {firstName}, your order #{orderNumber} is complete. Thank you for shopping with us!',
  },
  {
    key: 'orderCancelledEnabled',
    name: 'Order cancelled',
    description: 'Send SMS when order is cancelled',
    template: 'Hi {firstName}, your order #{orderNumber} has been cancelled. Contact us if you have questions.',
  },
];

const STATUS_PILL = {
  DELIVERED: 'sms-pill-success',
  SENT:      'sms-pill-info',
  PENDING:   'sms-pill-warn',
  FAILED:    'sms-pill-danger',
};

const INITIAL_SEGMENT = {
  segments: 0, charCount: 0, charsRemaining: 160, maxChars: 160,
  isUnicode: false, singleLimit: 160, multiLimit: 153,
};

function calculateSegmentInfo(message) {
  if (!message) return INITIAL_SEGMENT;
  const length = message.length;
  const isUnicode = /[^\x00-\x7F]/.test(message);
  const singleLimit = isUnicode ? 70  : 160;
  const multiLimit  = isUnicode ? 67  : 153;
  const segments = length <= singleLimit ? 1 : Math.ceil(length / multiLimit);
  let charsRemaining;
  if (length === 0)                  charsRemaining = singleLimit;
  else if (length <= singleLimit)    charsRemaining = singleLimit - length;
  else {
    const used = length % multiLimit;
    charsRemaining = used === 0 ? 0 : multiLimit - used;
  }
  const maxChars = segments <= 1 ? singleLimit : segments * multiLimit;
  return {
    segments: length === 0 ? 0 : segments,
    charCount: length, charsRemaining, maxChars,
    isUnicode, singleLimit, multiLimit,
  };
}

const formatDate = (date) => new Date(date).toLocaleDateString('en-US', {
  year: 'numeric', month: 'short', day: 'numeric',
  hour: '2-digit', minute: '2-digit',
});

export default function SMSPage() {
  const { getAxios } = useSecureAuth();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);

  const [dashboard, setDashboard] = useState(null);
  const [credits, setCredits] = useState({ balance: 0, totalPurchased: 0, totalUsed: 0 });
  const [pricePerCredit, setPricePerCredit] = useState(5);
  const [packages, setPackages] = useState([]);
  const [logs, setLogs] = useState([]);
  const [transactions, setTransactions] = useState([]);

  const [purchasing, setPurchasing] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    orderCreatedEnabled: false,
    orderShippedEnabled: false,
    orderDeliveredEnabled: false,
    orderCompletedEnabled: false,
    orderCancelledEnabled: false,
  });

  const [showSendModal, setShowSendModal] = useState(false);
  const [sendType, setSendType] = useState('single');
  const [sendingTo, setSendingTo] = useState('');
  const [sendingMessage, setSendingMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [smsSegmentInfo, setSmsSegmentInfo] = useState(INITIAL_SEGMENT);

  /* ── Fetchers ─────────────────────────────────────────── */
  const fetchDashboard = useCallback(async () => {
    try {
      const res = await getAxios().get('/merchant/sms/dashboard');
      if (res.data.success) {
        setDashboard(res.data.dashboard);
        setCredits(res.data.dashboard.credits || { balance: 0, totalPurchased: 0, totalUsed: 0 });
        if (res.data.dashboard.pricePerCredit) {
          setPricePerCredit(res.data.dashboard.pricePerCredit);
        }
      }
    } catch (err) { console.error('Fetch SMS dashboard error:', err); }
  }, [getAxios]);

  const fetchPackages = useCallback(async () => {
    try {
      const res = await getAxios().get('/merchant/sms/pricing');
      if (res.data.success) setPackages(res.data.packages);
    } catch (err) { console.error('Fetch SMS packages error:', err); }
  }, [getAxios]);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await getAxios().get('/merchant/sms/settings');
      if (res.data.success) {
        setSettingsForm({
          orderCreatedEnabled:   res.data.settings.orderCreatedEnabled   || false,
          orderShippedEnabled:   res.data.settings.orderShippedEnabled   || false,
          orderDeliveredEnabled: res.data.settings.orderDeliveredEnabled || false,
          orderCompletedEnabled: res.data.settings.orderCompletedEnabled || false,
          orderCancelledEnabled: res.data.settings.orderCancelledEnabled || false,
        });
      }
    } catch (err) { console.error('Fetch SMS settings error:', err); }
  }, [getAxios]);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await getAxios().get('/merchant/sms/logs?limit=50');
      if (res.data.success) setLogs(res.data.logs);
    } catch (err) { console.error('Fetch SMS logs error:', err); }
  }, [getAxios]);

  const fetchTransactions = useCallback(async () => {
    try {
      const res = await getAxios().get('/merchant/sms/transactions?limit=50');
      if (res.data.success) setTransactions(res.data.transactions);
    } catch (err) { console.error('Fetch SMS transactions error:', err); }
  }, [getAxios]);

  const fetchCustomerGroups = useCallback(async () => {
    try {
      const res = await getAxios().get('/customer-groups');
      setCustomerGroups(res.data || []);
    } catch (err) { console.error('Fetch customer groups error:', err); }
  }, [getAxios]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchPackages(), fetchSettings(), fetchCustomerGroups()]);
      setLoading(false);
    };
    load();
  }, [fetchDashboard, fetchPackages, fetchSettings, fetchCustomerGroups]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchLogs();
      fetchTransactions();
    }
  }, [activeTab, fetchLogs, fetchTransactions]);

  // Verify payment on return from Paystack
  useEffect(() => {
    const verifyPayment = async () => {
      const params = new URLSearchParams(window.location.search);
      const reference = params.get('verify') || params.get('reference') || params.get('trxref');
      if (!reference) return;
      try {
        setLoading(true);
        const res = await getAxios().post('/merchant/sms/purchase/verify', { reference });
        if (res.data.success) {
          toast.success(res.data.message || 'SMS credits added successfully');
          fetchDashboard();
          setActiveTab('dashboard');
        }
      } catch (err) {
        const msg = err.response?.data?.error || 'Payment verification failed';
        if (!msg.includes('already completed')) toast.error(msg);
      } finally {
        window.history.replaceState({}, document.title, window.location.pathname);
        setLoading(false);
      }
    };
    verifyPayment();
  }, [getAxios, toast, fetchDashboard]);

  /* ── Mutations ────────────────────────────────────────── */
  const handlePurchase = async (pkg) => {
    setSelectedPackage(pkg);
    setPurchasing(true);
    try {
      const res = await getAxios().post('/merchant/sms/purchase', { packageId: pkg.id });
      if (res.data.success && res.data.authorizationUrl) {
        window.location.href = res.data.authorizationUrl;
      } else {
        throw new Error('Failed to get payment URL');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to initiate purchase');
      setPurchasing(false);
      setSelectedPackage(null);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await getAxios().put('/merchant/sms/settings', settingsForm);
      if (res.data.success) toast.success('SMS settings saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSendSMS = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const axios = getAxios();
      const res = sendType === 'group'
        ? await axios.post('/merchant/sms/send-to-group', { groupId: selectedGroupId, message: sendingMessage })
        : await axios.post('/merchant/sms/send', { phoneNumber: sendingTo, message: sendingMessage });
      if (res.data.success) {
        toast.success(res.data.message || 'SMS sent');
        closeSendModal();
        fetchDashboard();
        fetchLogs();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const handleMessageChange = (e) => {
    const message = e.target.value;
    setSendingMessage(message);
    setSmsSegmentInfo(calculateSegmentInfo(message));
  };

  const handleToggleSetting = (key) => {
    setSettingsForm((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const closeSendModal = () => {
    setShowSendModal(false);
    setSendType('single');
    setSendingTo('');
    setSendingMessage('');
    setSelectedGroupId('');
    setSmsSegmentInfo(INITIAL_SEGMENT);
  };

  /* ── Derived ──────────────────────────────────────────── */
  const enabledCount = Object.values(settingsForm).filter(Boolean).length;
  const selectedGroup = customerGroups.find((g) => g.id === selectedGroupId);
  const lowCredits = (credits?.balance ?? 0) < 10;

  /* ── Render ───────────────────────────────────────────── */
  if (loading) {
    return (
      <Shell>
        <div className="sms-page">
          <div className="sms-loading">
            <Loader2 size={20} className="sms-spin" /> Loading SMS…
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="sms-page">

        {/* ── Header ────────────────────────────────────── */}
        <header className="sms-header">
          <div className="sms-header-text">
            <h1 className="sms-title">SMS notifications</h1>
            <p className="sms-subtitle">Send SMS to customers for order updates.</p>
          </div>
          <div className="sms-header-actions">
            <span className="sms-credit-pill" title="SMS credits available">
              <MessageSquare size={13} /> {credits?.balance || 0} credits
            </span>
            <button
              type="button"
              className="sms-btn sms-btn-primary"
              onClick={() => setShowSendModal(true)}
              disabled={!credits?.balance}
            >
              <Send size={14} /> <span className="sms-btn-text">Send SMS</span>
            </button>
          </div>
        </header>

        {/* ── Tabs (scrollable on mobile) ─────────────── */}
        <div className="sms-tabs-wrap">
          <div className="sms-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  type="button"
                  className={`sms-tab${activeTab === t.id ? ' is-active' : ''}`}
                  onClick={() => setActiveTab(t.id)}
                >
                  <Icon size={14} /> {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Dashboard tab ───────────────────────────── */}
        {activeTab === 'dashboard' && (
          <>
            <section className="sms-stat-grid">
              <div className="sms-stat">
                <span className="sms-stat-label">Available credits</span>
                <span className="sms-stat-value sms-stat-value-success">{credits?.balance || 0}</span>
              </div>
              <div className="sms-stat">
                <span className="sms-stat-label">SMS sent (30 days)</span>
                <span className="sms-stat-value">{dashboard?.analytics?.totalSent || 0}</span>
              </div>
              <div className="sms-stat">
                <span className="sms-stat-label">Delivery rate</span>
                <span className="sms-stat-value">{dashboard?.analytics?.deliveryRate || 0}%</span>
              </div>
              <div className="sms-stat">
                <span className="sms-stat-label">Auto-notifications</span>
                <span className="sms-stat-value">{enabledCount} <span className="sms-stat-sub">enabled</span></span>
              </div>
            </section>

            {lowCredits && (
              <div className="sms-warn-banner">
                <AlertTriangle size={16} />
                <div className="sms-warn-text">
                  <strong>Low SMS credits</strong>
                  <span>
                    You have {credits?.balance || 0} credit{credits?.balance === 1 ? '' : 's'} left.
                    Purchase more to keep sending notifications.
                  </span>
                </div>
                <button
                  type="button"
                  className="sms-btn sms-btn-primary"
                  onClick={() => setActiveTab('credits')}
                >
                  Buy credits
                </button>
              </div>
            )}

            <section className="sms-card">
              <div className="sms-card-head">
                <h2 className="sms-card-title">Quick actions</h2>
              </div>
              <ul className="sms-action-list">
                <li>
                  <button type="button" className="sms-action" onClick={() => setActiveTab('credits')}>
                    <span className="sms-action-icon"><CreditCard size={16} /></span>
                    <span className="sms-action-text">
                      <strong>Buy credits</strong>
                      <em>Top up to send more SMS notifications.</em>
                    </span>
                    <ChevronRight size={14} className="sms-action-chev" />
                  </button>
                </li>
                <li>
                  <button type="button" className="sms-action" onClick={() => setActiveTab('settings')}>
                    <span className="sms-action-icon"><Bell size={16} /></span>
                    <span className="sms-action-text">
                      <strong>Configure notifications</strong>
                      <em>Enable automatic SMS for order events.</em>
                    </span>
                    <ChevronRight size={14} className="sms-action-chev" />
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="sms-action"
                    onClick={() => setShowSendModal(true)}
                    disabled={!credits?.balance}
                  >
                    <span className="sms-action-icon"><Send size={16} /></span>
                    <span className="sms-action-text">
                      <strong>Send custom SMS</strong>
                      <em>Message a phone number or customer group.</em>
                    </span>
                    <ChevronRight size={14} className="sms-action-chev" />
                  </button>
                </li>
              </ul>
            </section>
          </>
        )}

        {/* ── Credits tab ─────────────────────────────── */}
        {activeTab === 'credits' && (
          <>
            <section className="sms-card">
              <div className="sms-card-head">
                <h2 className="sms-card-title">Current balance</h2>
              </div>
              <div className="sms-balance">
                <div className="sms-balance-main">
                  <span className="sms-balance-amount">{credits?.balance || 0}</span>
                  <span className="sms-balance-label">SMS credits</span>
                </div>
                <div className="sms-balance-mini">
                  <div>
                    <span className="sms-stat-label">Total purchased</span>
                    <span className="sms-stat-value">{credits?.totalPurchased || 0}</span>
                  </div>
                  <div>
                    <span className="sms-stat-label">Total used</span>
                    <span className="sms-stat-value">{credits?.totalUsed || 0}</span>
                  </div>
                </div>
              </div>
            </section>

            <section className="sms-card">
              <div className="sms-card-head">
                <h2 className="sms-card-title">Purchase messaging credits</h2>
              </div>
              {packages.length === 0 ? (
                <div className="sms-empty-inline">
                  <Package size={20} />
                  <span>No pricing packages available yet. Please contact admin.</span>
                </div>
              ) : (
                <>
                  <ul className="sms-pricing-list">
                    {packages.map((pkg) => {
                      const total = pkg.credits + (pkg.bonusCredits || 0);
                      return (
                        <li key={pkg.id} className="sms-pricing-row">
                          <span className="sms-pricing-credits">
                            <strong>{total.toLocaleString()}</strong> credits
                            {pkg.bonusCredits > 0 && (
                              <em className="sms-bonus">+{pkg.bonusCredits} bonus</em>
                            )}
                          </span>
                          <span className="sms-pricing-price">₦{pkg.price?.toLocaleString()}</span>
                        </li>
                      );
                    })}
                  </ul>

                  <div className="sms-purchase-form">
                    <label className="sms-field">
                      <span className="sms-field-label">How many credits do you want to purchase?</span>
                      <select
                        className="sms-input sms-select"
                        value={selectedPackage?.id || ''}
                        onChange={(e) => {
                          const pkg = packages.find((p) => p.id === e.target.value);
                          setSelectedPackage(pkg || null);
                        }}
                      >
                        <option value="">Select a package…</option>
                        {packages.map((pkg) => (
                          <option key={pkg.id} value={pkg.id}>
                            {(pkg.credits + (pkg.bonusCredits || 0)).toLocaleString()} credits — ₦{pkg.price?.toLocaleString()}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      className="sms-btn sms-btn-primary sms-btn-full"
                      onClick={() => selectedPackage && handlePurchase(selectedPackage)}
                      disabled={purchasing || !selectedPackage}
                    >
                      {purchasing ? <Loader2 size={14} className="sms-spin" /> : null}
                      {purchasing ? 'Processing…' : 'Continue to payment'}
                    </button>
                  </div>
                </>
              )}
            </section>
          </>
        )}

        {/* ── Settings tab ────────────────────────────── */}
        {activeTab === 'settings' && (
          <>
            <section className="sms-card">
              <div className="sms-card-head">
                <h2 className="sms-card-title">Auto-notification settings</h2>
              </div>
              <p className="sms-card-desc">
                Enable automatic SMS for order events. Credits are based on message length:
                1 credit per 160 characters (or 70 for Unicode).
              </p>

              <ul className="sms-toggle-list">
                {NOTIFICATIONS.map((n) => {
                  const checked = !!settingsForm[n.key];
                  return (
                    <li key={n.key} className="sms-toggle-row">
                      <div className="sms-toggle-info">
                        <h4 className="sms-toggle-name">{n.name}</h4>
                        <p className="sms-toggle-desc">{n.description}</p>
                        <code className="sms-template">{n.template}</code>
                      </div>
                      <label className="sms-switch" aria-label={`Toggle ${n.name}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleToggleSetting(n.key)}
                        />
                        <span className="sms-switch-track" />
                      </label>
                    </li>
                  );
                })}
              </ul>

              <div className="sms-card-footer">
                <button
                  type="button"
                  className="sms-btn sms-btn-primary"
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                >
                  {savingSettings ? <Loader2 size={14} className="sms-spin" /> : null}
                  {savingSettings ? 'Saving…' : 'Save settings'}
                </button>
              </div>
            </section>

            <section className="sms-card">
              <div className="sms-card-head">
                <h2 className="sms-card-title">Template variables</h2>
              </div>
              <p className="sms-card-desc">Custom templates can use these variables:</p>
              <ul className="sms-var-list">
                <li><code>{'{firstName}'}</code> Customer's first name</li>
                <li><code>{'{customerName}'}</code> Customer's full name</li>
                <li><code>{'{orderNumber}'}</code> Order number</li>
                <li><code>{'{storeName}'}</code> Your store name</li>
                <li><code>{'{total}'}</code> Order total amount</li>
              </ul>
            </section>
          </>
        )}

        {/* ── History tab ─────────────────────────────── */}
        {activeTab === 'history' && (
          <>
            <section className="sms-card">
              <div className="sms-card-head">
                <h2 className="sms-card-title">
                  Recent messages <span className="sms-card-count">({logs.length})</span>
                </h2>
              </div>
              {logs.length === 0 ? (
                <div className="sms-empty-inline">
                  <MessageSquare size={20} />
                  <span>No SMS messages sent yet.</span>
                </div>
              ) : (
                <ul className="sms-list">
                  {logs.map((log) => (
                    <li key={log.id} className="sms-row">
                      <div className="sms-log-row">
                        <span className="sms-log-recipient">
                          {log.recipientName && <strong>{log.recipientName}</strong>}
                          <em>{log.recipientPhone}</em>
                        </span>
                        <span className={`sms-pill ${STATUS_PILL[log.deliveryStatus] || 'sms-pill-muted'}`}>
                          {log.deliveryStatus}
                        </span>
                        <span className="sms-log-meta">
                          {log.messageType?.replace('_', ' ') || 'CUSTOM'}
                          {' · '}
                          {formatDate(log.createdAt)}
                        </span>
                        <span className="sms-log-message">{log.message}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="sms-card">
              <div className="sms-card-head">
                <h2 className="sms-card-title">
                  Credit transactions <span className="sms-card-count">({transactions.length})</span>
                </h2>
              </div>
              {transactions.length === 0 ? (
                <div className="sms-empty-inline">
                  <CreditCard size={20} />
                  <span>No transactions yet.</span>
                </div>
              ) : (
                <ul className="sms-list">
                  {transactions.map((tx) => {
                    const isCredit = tx.credits > 0;
                    return (
                      <li key={tx.id} className="sms-row">
                        <div className="sms-tx-row">
                          <span className={`sms-tx-icon${isCredit ? ' is-credit' : ' is-debit'}`}>
                            {isCredit ? '+' : '−'}
                          </span>
                          <span className="sms-tx-info">
                            <strong>{tx.description}</strong>
                            <em>{formatDate(tx.createdAt)}</em>
                          </span>
                          <span className={`sms-tx-amount${isCredit ? ' is-credit' : ' is-debit'}`}>
                            {isCredit ? '+' : ''}{tx.credits} credits
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </>
        )}

        {/* ── Send SMS Modal ──────────────────────────── */}
        <Modal
          isOpen={showSendModal}
          onClose={closeSendModal}
          title="Send SMS"
          size="md"
          padded={false}
          footer={
            <>
              <button type="button" className="sms-btn sms-btn-secondary" onClick={closeSendModal}>
                Cancel
              </button>
              <button
                type="submit"
                form="sms-send-form"
                className="sms-btn sms-btn-primary"
                disabled={
                  sending ||
                  (sendType === 'group' && !selectedGroupId) ||
                  smsSegmentInfo.segments === 0
                }
              >
                {sending ? <Loader2 size={14} className="sms-spin" /> : <Send size={14} />}
                {sending
                  ? 'Sending…'
                  : sendType === 'group' && selectedGroup
                  ? `Send to ${selectedGroup.customerCount || 0} customers (₦${(((selectedGroup.customerCount || 0) * (smsSegmentInfo.segments || 1) * pricePerCredit)).toLocaleString()})`
                  : `Send (₦${(((smsSegmentInfo.segments || 1) * pricePerCredit)).toLocaleString()})`}
              </button>
            </>
          }
        >
          <form id="sms-send-form" className="sms-form" onSubmit={handleSendSMS}>
            <div className="sms-field">
              <span className="sms-field-label">Send to</span>
              <div className="sms-segmented" role="group" aria-label="Recipient type">
                <button
                  type="button"
                  className={`sms-seg-btn${sendType === 'single' ? ' is-active' : ''}`}
                  onClick={() => setSendType('single')}
                >
                  Single phone
                </button>
                <button
                  type="button"
                  className={`sms-seg-btn${sendType === 'group' ? ' is-active' : ''}`}
                  onClick={() => setSendType('group')}
                >
                  Customer group
                </button>
              </div>
            </div>

            {sendType === 'single' && (
              <div className="sms-field">
                <label className="sms-field-label">Phone number</label>
                <input
                  type="tel"
                  className="sms-input"
                  placeholder="e.g. 08012345678"
                  value={sendingTo}
                  onChange={(e) => setSendingTo(e.target.value)}
                  required
                />
              </div>
            )}

            {sendType === 'group' && (
              <div className="sms-field">
                <label className="sms-field-label">Customer group</label>
                {customerGroups.length === 0 ? (
                  <div className="sms-empty-inline">
                    <span>No customer groups found.</span>
                    <a href="/customers" className="sms-inline-link">Create a group first</a>
                  </div>
                ) : (
                  <>
                    <select
                      className="sms-input sms-select"
                      value={selectedGroupId}
                      onChange={(e) => setSelectedGroupId(e.target.value)}
                      required
                    >
                      <option value="">Select a group…</option>
                      {customerGroups.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name} ({g.customerCount || 0} customers)
                        </option>
                      ))}
                    </select>
                    {selectedGroup && (
                      <div className="sms-group-info">
                        <strong>{selectedGroup.customerCount || 0} recipients</strong>
                        <span>
                          Estimated cost: ₦{((selectedGroup.customerCount || 0) * (smsSegmentInfo.segments || 1) * pricePerCredit).toLocaleString()}
                          {smsSegmentInfo.segments > 1 && ` (${smsSegmentInfo.segments} segments × ₦${pricePerCredit})`}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="sms-field">
              <label className="sms-field-label">Message</label>
              <textarea
                className="sms-input sms-textarea"
                placeholder="Type your message…"
                value={sendingMessage}
                onChange={handleMessageChange}
                rows={4}
                required
              />
              <div className="sms-segment-info">
                <span className={`sms-seg-char${smsSegmentInfo.segments > 1 ? ' is-multi' : ''}`}>
                  {smsSegmentInfo.charCount} character{smsSegmentInfo.charCount !== 1 ? 's' : ''}
                  {smsSegmentInfo.isUnicode && <span className="sms-unicode-tag">Unicode</span>}
                </span>
                <span className="sms-seg-cost">
                  {smsSegmentInfo.segments > 0
                    ? <>{smsSegmentInfo.segments} segment{smsSegmentInfo.segments !== 1 ? 's' : ''} = <strong>₦{(smsSegmentInfo.segments * pricePerCredit).toLocaleString()}</strong></>
                    : '₦0'}
                </span>
              </div>
              {smsSegmentInfo.segments > 1 && (
                <span className="sms-field-hint">
                  Messages over {smsSegmentInfo.singleLimit} characters use multiple segments
                  (₦{pricePerCredit} per segment).
                </span>
              )}
            </div>
          </form>
        </Modal>

      </div>
    </Shell>
  );
}
